import type { JSX } from '@builder.io/qwik';
import { useContext } from '@builder.io/qwik';
import { component$ } from '@builder.io/qwik';
import type { LinksInterface } from '~/types/AppData/Layout/LinksInterface';
import More from '~/icons/more';
import { AppDataContext } from '~/contexts/AppDataContext';
import { cva } from 'class-variance-authority';
import { getCSSColorSVGFill, getCSSColorSVGStroke } from '~/utilities/getCSS';
import type { COLOR } from '~/types/AppData/Theme/ColorInterface';

const columnHeadingClasses = cva('flex font-group-tiny !font-bold mb-1', {
  variants: {
    multipleColumns: {
      true: 'w-[150px]',
      false: 'min-w-[200px]',
    },
  },
});

const columnItemClasses = cva('w-leading-[19px]', {
  variants: {
    multipleColumns: {
      true: 'w-[150px]',
      false: 'min-w-[200px]',
    },
  },
});

const buttonClasses = cva('flex', {
  variants: {
    iconStroke: getCSSColorSVGStroke(),
    iconFill: getCSSColorSVGFill(),
  },
});

export default component$(
  ({
    menu,
    iconColor,
  }: {
    menu: LinksInterface;
    iconColor: COLOR;
  }): JSX.Element => {
    const appData = useContext(AppDataContext);
    const siteName = appData.payloadToSSR.propertySettings.siteName;

    // Use style so that we can unset inset and THEN control positioning - the order is very important with popovers
    const style = {
      inset: 'unset',
      top: '77px',
      left: '50%',
      transform: 'translateX(-50%)',
    };

    // Checks to see if any menu items have children
    const multipleColumns = menu.value.some(
      (each) => each.children && each.children.length > 1
    );

    // Sometimes there are megamenus where one item has children and the rest are single links
    // Those single links need to be grouped together
    const singleLinks = menu.value.filter((each) => !each.children);

    if (!multipleColumns) {
      // If there is only one column, then nest that column to align it with the expectation of the renderer below
      menu.value = [
        {
          children: menu.value,
          link: { text: siteName ? `More from ${siteName}` : 'More', url: '' },
        },
      ];
    } else if (singleLinks.length) {
      // If there are single links, then add them to the end of the menu, nested under a new column
      // https://github.com/FTBpro/mmservices/blob/d12d6b371c8e9f7ffdc07b7bc0d758fd434762f7/site/site-ui-components-library/src/base/components/navigation/header/threeRowsHeader/megaMenu.tsx#L126
      menu.value = [
        ...menu.value.filter((each) => each.children),
        {
          children: singleLinks,
          link: { text: siteName ? `More from ${siteName}` : 'More', url: '' },
        },
      ];
    }

    return (
      <div>
        <button
          class={buttonClasses({ iconStroke: iconColor, iconFill: iconColor })}
          popovertarget='megamenu'
        >
          <More />
        </button>
        <div
          id='megamenu'
          style={style}
          class='p-6 shadow-megamenu w-max'
          popover='auto'
        >
          <div class='flex gap-6'>
            {menu.value.map((each, index) => {
              return (
                <div class='flex' key={index}>
                  <div>
                    <strong class={columnHeadingClasses({ multipleColumns })}>
                      {each.link.text}
                    </strong>
                    <ul class='flex flex-wrap flex-col gap-1 max-h-[260px]'>
                      {each.children?.map((item, itemIndex) => {
                        return (
                          <li
                            class={columnItemClasses({ multipleColumns })}
                            key={itemIndex}
                          >
                            <a
                              class='megamenu-link font-group-tiny leading-[19px] hover:text-primary'
                              href={item.link.url}
                            >
                              {item.link.text}
                            </a>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                  {index < menu.value.length - 1 && (
                    <div class='w-[1px] bg-grey h-full' />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }
);
